import { css } from "lit";
import { baseStyles } from "../../../theme/theme";
import { SEAT_COLS } from "../../data/mock-pricing";

export const bookingCheckoutStyles = [
  baseStyles,
  css`
    :host {
      display: block;
      background: var(--zui-bg, #fff);
      font-family: var(--zui-font), system-ui, sans-serif;
    }
    .page {
      max-width: 1080px;
      margin: 0 auto;
      padding: 1.75rem 1.5rem 3.5rem;
    }
    .grid {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 360px;
      gap: 1.5rem;
      align-items: start;
    }
    @media (max-width: 860px) {
      .grid {
        /* minmax(0,…) not bare 1fr: bare 1fr is minmax(auto,1fr), whose auto
             floor is the summary card's min-content (~344px) and overflows the
             viewport on phones. */
        grid-template-columns: minmax(0, 1fr);
      }
      /* PLAT-6800: the Order Summary stays in source order — after the steps —
           on one column. Forcing it first put a price in front of visitors
           before they had chosen anything to pay for. */
    }
    .col {
      display: flex;
      flex-direction: column;
      gap: 0.875rem;
      min-width: 0;
    }
    .card {
      background: #fff;
      border: 1px solid var(--zui-line);
      border-radius: 14px;
    }
    /* class header card */
    .class-card {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 1.125rem;
    }
    .class-card .thumb {
      width: 64px;
      height: 64px;
      border-radius: 12px;
      overflow: hidden;
      flex: 0 0 auto;
      background: var(--zui-accent-soft);
      display: grid;
      place-items: center;
    }
    .class-card .thumb img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    /* Initials shown when the class/instructor has no thumbnail image, so the
         placeholder is never an empty colored square. */
    .thumb-initials {
      font-family: var(--zui-font), system-ui, sans-serif;
      font-weight: 700;
      line-height: 1;
      color: var(--zui-accent-deep, var(--zui-accent));
      text-transform: uppercase;
    }
    .class-card .thumb-initials {
      font-size: 1.15rem;
    }
    .sum-item .thumb-initials {
      font-size: 1rem;
    }
    .class-card .ov {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--zui-panel-ink-soft);
    }
    .class-card .cname {
      font-family: var(--zui-font), system-ui, sans-serif;
      font-weight: 700;
      font-size: 22px;
      letter-spacing: -0.01em;
      color: var(--zui-headline);
      margin: 2px 0;
    }
    .class-card .cmeta {
      font-size: 13px;
      color: var(--zui-panel-ink-soft);
    }
    .back {
      appearance: none;
      background: none;
      border: 0;
      color: var(--zui-accent);
      cursor: pointer;
      font: inherit;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      padding: 0;
      align-self: flex-start;
      margin-bottom: 0.25rem;
    }
    /* sign-in banner */
    .signin-banner {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.875rem 1.125rem;
      border-radius: 12px;
      border: 1px solid var(--zui-accent);
      background: var(--zui-accent-soft);
    }
    .signin-banner .ic {
      color: var(--zui-accent-deep);
      flex: 0 0 auto;
    }
    .signin-banner .banner-copy {
      flex: 1 1 200px;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .signin-banner .t {
      font-weight: 700;
      font-size: 0.9rem;
      color: var(--zui-headline);
      line-height: 1.2;
    }
    .signin-banner .s {
      font-size: 0.8rem;
      line-height: 1.4;
      color: var(--zui-panel-ink-soft);
    }
    /* 7/25 QA: the accent-pill '.signin-banner button' rule that used to live
         here is gone. The signed-out "Sign in" call to action moved onto the
         step-1 headline row as the shared '.zui-btn-secondary' (see
         'renderSignInCta()'), so the banner is now a signed-in-only status
         panel and the pill — which competed with the primary "Confirm booking"
         CTA for weight — has no element left to style. */
    /* CK1: subtle "log out" affordance shown next to the welcome message when
         a member is signed in — a plain accent-colored text link. */
    .signin-banner button.logout-link {
      margin-left: auto;
      flex: 0 0 auto;
      background: none;
      color: var(--zui-accent);
      border: 0;
      border-radius: 0;
      min-height: 0;
      padding: 0;
      font-weight: 700;
      font-size: 0.8rem;
      text-decoration: underline;
      white-space: nowrap;
      cursor: pointer;
    }
    /* Narrow phones: the banner's flex row (icon + copy + log-out link) can
         run out of room for all three inline, so let it wrap and tighten the
         copy's line-height so short-viewport wraps don't look loosely spaced.
         Same "meta" line-height fix applied to the class-card's
         instructor/time line just below it.

         The full-width '.signin-banner button' stacking rule that used to sit
         here went with the accent pill — the banner is signed-in-only now, and
         the log-out text link is small enough to sit inline at any width. */
    @media (max-width: 480px) {
      .signin-banner {
        flex-wrap: wrap;
        row-gap: 0.625rem;
      }
      .signin-banner .t {
        line-height: 1.4;
      }
      .class-card {
        gap: 0.75rem;
        padding: 1rem;
      }
      .class-card .cname {
        font-size: 18px;
      }
      .class-card .cmeta {
        line-height: 1.5;
      }
    }
    /* step card */
    .step {
      padding: 1.125rem 1.25rem;
      transition:
        box-shadow 0.15s ease,
        border-color 0.15s ease;
    }
    .step.open {
      border-color: var(--zui-accent);
      box-shadow: 0 0 0 3px var(--zui-accent-soft);
    }
    .step-head {
      display: flex;
      align-items: center;
      /* 'flex-wrap' rather than a width breakpoint: this embed is a custom
           element sized by whatever container the host page drops it in, so a
           viewport media query cannot tell how much room the row actually has.
           Wrapping drops the sign-in action onto its own line exactly when the
           step title no longer fits beside it, at any embed width. */
      flex-wrap: wrap;
      gap: 0.5rem 0.7rem;
      cursor: pointer;
    }
    /* 7/25 QA: the sign-in affordance is a real (secondary) button on the
         step headline row — the same treatment the events register flow and
         the shop checkout use — rather than an accent pill carrying the same
         visual weight as the primary "Confirm booking" CTA. Prompt + button
         travel together so they wrap as one unit. */
    .signin-cta {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 0.5rem 0.75rem;
      margin-left: auto;
      cursor: auto;
    }
    .signin-cta-t {
      font-size: 13px;
      color: var(--zui-panel-ink-soft);
    }
    .num {
      width: 26px;
      height: 26px;
      border-radius: 999px;
      display: grid;
      place-items: center;
      font-size: 0.8rem;
      font-weight: 700;
      /* Neutralize any line-height leaking in from the host page so the
           number glyph sits dead-center in the circle instead of riding high. */
      line-height: 1;
      background: transparent;
      color: var(--zui-panel-ink-soft);
      border: 1px solid var(--zui-line);
      flex: 0 0 auto;
    }
    .num.open {
      background: var(--zui-accent);
      color: var(--zui-btn-text);
      border-color: var(--zui-accent);
    }
    .num.done {
      background: #3a7a4d;
      color: #fff;
      border-color: #3a7a4d;
    }
    .step-title {
      font-family: var(--zui-font), system-ui, sans-serif;
      font-weight: 700;
      font-size: 16px;
      color: var(--zui-headline);
    }
    .edit {
      margin-left: auto;
      appearance: none;
      background: none;
      border: 0;
      color: var(--zui-accent);
      font: inherit;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      cursor: pointer;
    }
    .step-sum {
      font-size: 13px;
      color: var(--zui-panel-ink-soft);
      margin: 0.3rem 0 0 2.1rem;
      overflow-wrap: anywhere;
    }
    .body {
      margin: 0.85rem 0 0 2.1rem;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    @media (max-width: 860px) {
      .body {
        margin-left: 0;
      }
    }
    /* fields */
    label.fld {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      font-size: 0.78rem;
      font-weight: 600;
      color: var(--zui-panel-ink-soft);
      /* Let the field shrink below the input's intrinsic width so 2-up rows
           don't overflow on narrow (mobile) viewports. */
      min-width: 0;
    }
    .grid2 > * {
      min-width: 0;
    }
    input[type="text"],
    input[type="email"],
    input[type="tel"],
    input[type="date"],
    select.fld-select,
    textarea {
      width: 100%;
      min-width: 0;
      max-width: 100%;
      box-sizing: border-box;
      padding: 0.6rem 0.7rem;
      border: 1.5px solid var(--zui-line);
      border-radius: 12px;
      background: var(--zui-panel);
      color: var(--zui-headline);
      font: inherit;
      font-size: 16px;
    }
    input[type="date"] {
      appearance: none;
      -webkit-appearance: none;
      display: block;
      min-height: 44px;
    }
    input:focus,
    select.fld-select:focus {
      outline: none;
      border-color: var(--zui-accent);
      box-shadow: 0 0 0 3px var(--zui-accent-soft);
    }
    .grid2 {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.6rem;
    }
    /* Stack the 2-up field rows into a single column on phones so labels and
         values (e.g. email) aren't cramped/truncated. */
    @media (max-width: 540px) {
      .grid2 {
        grid-template-columns: 1fr;
      }
    }
    /* S6: on small screens the details step's paragraph copy — the consent
         lines and the "I'm booking this for someone else" descriptions/help —
         had too much space between wrapped lines. Tighten their line-height;
         desktop spacing is fine. */
    @media (max-width: 540px) {
      .consent,
      .others-toggle .s,
      .self-row .s,
      .count-row .s,
      .person .help {
        line-height: 1.25;
      }
    }
    .consent {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      font-size: 11.5px;
      color: var(--zui-panel-ink-soft);
      line-height: 1.4;
    }
    /* booking-for-others dashed card */
    .others {
      border: 1.5px dashed var(--zui-line);
      border-radius: 14px;
      padding: 1rem;
    }
    .others.on {
      border-color: var(--zui-accent);
      background: var(--zui-accent-soft);
    }
    .others-toggle {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      width: 100%;
      appearance: none;
      background: none;
      border: 0;
      cursor: pointer;
      text-align: left;
      font: inherit;
      padding: 0;
      /* X4: title + subtitle are inline siblings that wrap into one
           paragraph; the line-box strut inherits the host page's line-height,
           so it read very tall (img-78) even though the subtitle set its own
           1.35. Pin the strut tight. */
      line-height: 1.3;
    }
    .others-toggle .ic {
      width: 36px;
      height: 36px;
      border-radius: 999px;
      background: #fff;
      color: var(--zui-accent-deep);
      display: grid;
      place-items: center;
      flex: 0 0 auto;
      border: 1px solid var(--zui-line);
    }
    .others-toggle .t {
      font-weight: 700;
      font-size: 15px;
      color: var(--zui-headline);
    }
    .others-toggle .s {
      font-size: 12.5px;
      color: var(--zui-panel-ink-soft);
      margin-top: 1px;
      line-height: 1.35;
    }
    .switch {
      margin-left: auto;
      flex: 0 0 auto;
      width: 38px;
      height: 22px;
      border-radius: 999px;
      background: var(--zui-line);
      position: relative;
      transition: background 0.15s ease;
    }
    .switch.on {
      background: var(--zui-accent);
    }
    .switch::after {
      content: "";
      position: absolute;
      top: 2px;
      left: 2px;
      width: 18px;
      height: 18px;
      border-radius: 999px;
      background: #fff;
      transition: transform 0.15s ease;
    }
    .switch.on::after {
      transform: translateX(16px);
    }
    .scenario-row {
      display: flex;
      gap: 1.25rem;
      margin-top: 0.75rem;
    }
    .scenario-summary {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      margin-top: 0.75rem;
      font-size: 0.9rem;
      color: var(--zui-panel-ink, #333);
    }
    /* "Who are you booking for?" modal */
    .rc-overlay {
      position: fixed;
      inset: 0;
      z-index: 2147483000;
      background: rgba(20, 24, 28, 0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1rem;
    }
    .rc-sheet {
      background: #fff;
      border-radius: 16px;
      width: 100%;
      max-width: 380px;
      padding: 1.125rem;
      box-shadow: 0 24px 60px -20px rgba(0, 0, 0, 0.45);
    }
    .rc-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-family: var(--zui-font), system-ui, sans-serif;
      font-weight: 700;
      font-size: 1.05rem;
      color: var(--zui-headline);
      margin-bottom: 0.875rem;
    }
    .rc-close {
      appearance: none;
      background: none;
      border: 0;
      font: inherit;
      font-size: 1.2rem;
      line-height: 1;
      color: var(--zui-panel-ink-soft);
      cursor: pointer;
    }
    .rc-choices {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.625rem;
    }
    /* Card-shaped radio. The two options are mutually exclusive, so each card
         wraps a real (visually hidden) radio input sharing one name — that is
         what gives the group arrow-key navigation and a single tabstop for
         free. The card itself keeps the selected look via .sel. */
    .rc-choice {
      appearance: none;
      position: relative; /* anchors the visually-hidden .rc-radio */
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
      text-align: left;
      font: inherit;
      border: 1.5px solid var(--zui-line);
      border-radius: 12px;
      background: #fff;
      padding: 0.75rem 0.8rem;
      cursor: pointer;
    }
    .rc-choice.sel {
      border-color: var(--zui-accent);
      background: var(--zui-accent-soft);
    }
    .rc-choice:focus-within {
      border-color: var(--zui-accent);
      box-shadow: 0 0 0 3px var(--zui-accent-soft);
    }
    .rc-radio {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      overflow: hidden;
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      white-space: nowrap;
    }
    .rc-choice-t {
      font-weight: 700;
      font-size: 0.95rem;
      color: var(--zui-headline);
    }
    .rc-choice-s {
      font-size: 0.75rem;
      color: var(--zui-panel-ink-soft);
    }
    .rc-list {
      margin: 0.875rem 0;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      max-height: 40vh;
      overflow-y: auto;
    }
    .rc-opt {
      appearance: none;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      border: 1px solid var(--zui-line);
      border-radius: 10px;
      background: #fff;
      font: inherit;
      font-size: 0.95rem;
      color: var(--zui-headline);
      padding: 0.7rem 0.75rem;
      cursor: pointer;
    }
    .rc-opt.sel {
      border-color: var(--zui-accent);
      color: var(--zui-accent-deep, var(--zui-accent));
      font-weight: 700;
    }
    .rc-empty {
      font-size: 0.85rem;
      color: var(--zui-panel-ink-soft);
      padding: 0.5rem 0.25rem;
    }
    .checkbox-box {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      appearance: none;
      background: none;
      border: 0;
      font: inherit;
      font-size: 0.9rem;
      color: var(--zui-headline);
      cursor: pointer;
    }
    .checkbox-box .box {
      width: 16px;
      height: 16px;
      border-radius: 4px;
      border: 1.5px solid var(--zui-line);
      display: grid;
      place-items: center;
      color: transparent;
    }
    .checkbox-box[aria-pressed="true"] .box {
      background: var(--zui-accent);
      border-color: var(--zui-accent);
      color: #fff;
    }
    .self-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      border: 1px solid var(--zui-line);
      border-radius: 12px;
      background: #fff;
      padding: 0.75rem;
      margin-top: 0.75rem;
    }
    .self-row .t {
      font-weight: 600;
      font-size: 0.9rem;
      color: var(--zui-headline);
    }
    .self-row .s {
      font-size: 12.5px;
      color: var(--zui-panel-ink-soft);
      margin-top: 1px;
    }
    .count-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      border: 1px solid var(--zui-line);
      border-radius: 12px;
      background: #fff;
      padding: 0.75rem;
      margin-top: 0.6rem;
    }
    .count-row .t {
      font-weight: 600;
      font-size: 0.9rem;
      color: var(--zui-headline);
    }
    .count-row .s {
      font-size: 12.5px;
      color: var(--zui-panel-ink-soft);
      margin-top: 1px;
    }
    .stepper {
      margin-left: auto;
      display: inline-flex;
      align-items: center;
      gap: 0.6rem;
    }
    .stepper button {
      width: 32px;
      height: 32px;
      border-radius: 999px;
      border: 1px solid var(--zui-accent);
      background: #fff;
      color: var(--zui-accent-deep);
      cursor: pointer;
      font: inherit;
      font-size: 1.1rem;
      /* N5: center the −/+ glyph dead-center in the circular button. Without
           an explicit flex box + line-height:1 the glyph inherits the host
           page's line-height and rides high/off-center (same fix as .num). */
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      padding: 0;
    }
    .stepper .n {
      font-family: var(--zui-font), system-ui, sans-serif;
      font-weight: 700;
      min-width: 1.2rem;
      text-align: center;
    }
    .person {
      border: 1px solid var(--zui-line);
      border-radius: 12px;
      background: #fff;
      padding: 0.85rem;
      margin-top: 0.6rem;
    }
    /* CK3: the family/guest add form's rows (name / DOB / email+phone / help)
         sat flush against each other. Space them out — especially once the
         2-up rows stack into a single column on small screens. */
    .person.person-add {
      display: flex;
      flex-direction: column;
      gap: 0.9rem;
    }
    .person.person-add .ph {
      margin: 0;
    }
    .person.person-add .help {
      margin-top: 0;
    }
    .person .ph {
      display: flex;
      align-items: center;
      margin: 0 0 0.5rem;
    }
    .person h5 {
      margin: 0;
      font-size: 0.72rem;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--zui-panel-ink-soft);
    }
    .person .rm {
      margin-left: auto;
      appearance: none;
      background: none;
      border: 0;
      color: var(--zui-accent);
      font: inherit;
      font-size: 0.75rem;
      cursor: pointer;
    }
    .person .help {
      font-size: 11.5px;
      color: var(--zui-panel-ink-soft);
      margin-top: 0.4rem;
      line-height: 1.4;
    }
    .add-link {
      appearance: none;
      background: none;
      border: 0;
      color: var(--zui-accent);
      font: inherit;
      font-size: 0.82rem;
      font-weight: 700;
      cursor: pointer;
      margin-top: 0.5rem;
      align-self: flex-start;
    }
    /* waiver accordion */
    .waiver {
      border: 1px solid var(--zui-line);
      border-radius: 12px;
      overflow: hidden;
      background: var(--zui-bg, #fff);
    }
    .waiver-head {
      display: flex;
      align-items: flex-start;
      gap: 0.625rem;
      width: 100%;
      appearance: none;
      background: none;
      border: 0;
      text-align: left;
      cursor: pointer;
      padding: 0.75rem 0.875rem;
      font: inherit;
    }
    .waiver-head .ic {
      width: 28px;
      height: 28px;
      border-radius: 8px;
      background: var(--zui-accent-soft);
      color: var(--zui-accent-deep);
      display: grid;
      place-items: center;
      flex: 0 0 auto;
    }
    .waiver-head .t {
      font-size: 13.5px;
      font-weight: 700;
      color: var(--zui-headline);
      /* SC10: pin a tight line-height so the inherited host-page line-height
           doesn't open a loose gap below the title before "Tap to read". */
      line-height: 1.2;
    }
    .waiver-head .s {
      font-size: 11.5px;
      color: var(--zui-panel-ink-soft);
      margin-top: 1px;
      line-height: 1.2;
    }
    .waiver-head .chev {
      margin-left: auto;
      color: var(--zui-panel-ink-soft);
      transition: transform 0.2s ease;
    }
    .waiver-head .chev.open {
      transform: rotate(180deg);
    }
    .waiver-body {
      max-height: 200px;
      overflow: auto;
      padding: 0 0.875rem 0.875rem;
      font-size: 13px;
      line-height: 1.15;
      color: var(--zui-panel-ink-soft);
    }
    .waiver-body p {
      margin: 0 0 0.45rem;
    }
    .waiver-body p:last-child {
      margin-bottom: 0;
    }
    .waiver-detail {
      font-size: 13px;
      line-height: 1.55;
      color: var(--zui-panel-ink-soft);
      margin: 0 0 0.75rem;
      white-space: pre-line;
    }
    .waiver-check {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.75rem 0.875rem;
      border-top: 1px solid var(--zui-line);
      font-size: 12.5px;
      color: var(--zui-headline);
      line-height: 1.15;
      margin-top: 0.5rem;
    }
    /* ── real form questions (waiver + intake) ──
         The waiver is a FORM, not a paragraph plus a tick box: every
         non-identity question on it is rendered with the control its type
         calls for, mirroring the dashboard renderer
         (libs/shared-ux/src/components/hvc/AthleteFormView.tsx). */
    .waiver-fields {
      display: flex;
      flex-direction: column;
      gap: 0.7rem;
      padding: 0.25rem 0.875rem 0.75rem;
    }
    /* The same questions, asked for an extra attendee inside their own card
         (see renderAttendeeForms). No padding of its own: .person-add
         already spaces its rows, and the fields line up with the name/email
         inputs directly above them. */
    .attendee-form {
      display: flex;
      flex-direction: column;
      gap: 0.9rem;
    }
    div.fld {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      font-size: 0.78rem;
      font-weight: 600;
      color: var(--zui-panel-ink-soft);
      min-width: 0;
    }
    .fld-req {
      color: #b4541f;
      margin-left: 0.15rem;
    }
    .fld-err {
      font-size: 0.72rem;
      font-weight: 500;
      line-height: 1.3;
      color: #b4541f;
      margin-top: 0.2rem;
    }
    label.fld.invalid input,
    label.fld.invalid textarea,
    label.fld.invalid select.fld-select {
      border-color: #b4541f;
    }
    /* single-select / multi-select render as toggle chips, the same
         affordance the dashboard uses, because a native select cannot express
         a multi-select answer. */
    .opt-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 0.4rem;
      margin-top: 0.1rem;
    }
    .opt-chip {
      appearance: none;
      border: 1.5px solid var(--zui-line);
      border-radius: 999px;
      background: var(--zui-panel);
      color: var(--zui-headline);
      font: inherit;
      font-size: 13px;
      font-weight: 500;
      padding: 0.35rem 0.75rem;
      cursor: pointer;
    }
    .opt-chip[aria-pressed="true"] {
      border-color: var(--zui-accent);
      background: var(--zui-accent-soft);
    }
    .opt-chips.invalid .opt-chip {
      border-color: #b4541f;
    }
    /* An electronic signature is the signer's TYPED full legal name (see
         renderIntakeField) — styled to read as a signature line. */
    input.sig-input {
      font-family: cursive, serif;
      font-size: 17px;
      font-style: italic;
      border-style: dashed;
    }
    .sig-hint {
      font-size: 0.7rem;
      font-weight: 500;
      color: var(--zui-panel-ink-soft);
      margin-top: 0.15rem;
    }
    /* seat grid */
    .chips {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
    .seat-chip {
      appearance: none;
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      border: 1px solid var(--zui-line);
      border-radius: 999px;
      background: #fff;
      font: inherit;
      font-size: 0.8rem;
      padding: 0.3rem 0.7rem 0.3rem 0.3rem;
      cursor: pointer;
      color: var(--zui-headline);
    }
    .seat-chip[aria-pressed="true"] {
      border-color: var(--zui-accent);
      background: var(--zui-accent-soft);
    }
    .seat-chip .n {
      width: 20px;
      height: 20px;
      border-radius: 999px;
      background: var(--zui-panel);
      display: grid;
      place-items: center;
      font-size: 0.7rem;
      font-weight: 700;
    }
    .seat-chip .seatval {
      color: var(--zui-accent-deep);
      font-weight: 700;
    }
    .front {
      text-align: center;
      font-size: 0.7rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--zui-panel-ink-soft);
      background: var(--zui-panel);
      border: 1px solid var(--zui-line);
      border-radius: 6px;
      padding: 0.3rem;
      margin: 0.75rem auto;
      max-width: 320px;
    }
    .seat-rows {
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
      max-width: 360px;
    }
    .seat-row {
      display: flex;
      align-items: center;
      gap: 0.4rem;
    }
    .seat-row .rl {
      width: 16px;
      font-size: 0.7rem;
      color: var(--zui-panel-ink-soft);
      flex: 0 0 auto;
    }
    .seat-cells {
      display: grid;
      grid-template-columns: repeat(${SEAT_COLS}, 1fr);
      gap: 0.4rem;
      flex: 1;
    }
    .seat {
      aspect-ratio: 1;
      border: 1.5px solid var(--zui-line);
      border-radius: 8px;
      background: #fff;
      color: var(--zui-panel-ink-soft);
      font-size: 0.7rem;
      font-weight: 700;
      cursor: pointer;
    }
    .seat.taken {
      background: #e7e7e3;
      color: var(--zui-panel-ink-soft);
      cursor: not-allowed;
    }
    .seat.mine {
      background: var(--zui-accent);
      color: var(--zui-btn-text);
      border-color: var(--zui-accent);
    }
    /* Designed room layout (seat map). Colors follow .seat / .taken / .mine. */
    .room-map svg {
      display: block;
      width: 100%;
      max-width: 520px;
      height: auto;
      max-height: 420px;
      margin: 0.5rem auto 0;
    }
    .room-map .shape {
      vector-effect: non-scaling-stroke;
      stroke-width: 1.5px;
    }
    .room-map text {
      font-weight: 700;
      pointer-events: none;
      user-select: none;
    }
    .room-spot {
      cursor: pointer;
      outline: none;
    }
    .room-spot .shape {
      fill: #fff;
      stroke: var(--zui-line);
    }
    .room-spot text {
      fill: var(--zui-panel-ink-soft);
    }
    .room-spot:focus-visible .shape {
      stroke: var(--zui-accent);
      stroke-width: 3px;
    }
    .room-spot.taken {
      cursor: not-allowed;
    }
    .room-spot.taken .shape {
      fill: #e7e7e3;
    }
    .room-spot.mine .shape {
      fill: var(--zui-accent);
      stroke: var(--zui-accent);
    }
    .room-spot.mine text {
      fill: var(--zui-btn-text);
    }
    .room-obstruction .shape {
      fill: #f3f4f6;
      stroke: #9ca3af;
      stroke-dasharray: 4 3;
    }
    .room-obstruction text {
      fill: #6b7280;
      font-weight: 600;
    }
    .room-unplaced-title {
      margin: 0.75rem 0 0.4rem;
      font-size: 0.72rem;
      font-weight: 700;
      color: var(--zui-panel-ink-soft);
    }
    .legend {
      display: flex;
      gap: 1rem;
      margin-top: 0.75rem;
      font-size: 0.72rem;
      color: var(--zui-panel-ink-soft);
    }
    .legend span {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
    }
    .legend .sw {
      width: 12px;
      height: 12px;
      border-radius: 3px;
      border: 1px solid var(--zui-line);
    }
    .legend .sw.mine {
      background: var(--zui-accent);
      border-color: var(--zui-accent);
    }
    .legend .sw.taken {
      background: #e7e7e3;
    }
    /* tiers */
    .payment-person {
      min-width: 0;
    }
    .payment-person + .payment-person {
      padding-top: 1rem;
      border-top: 1px solid var(--zui-line);
    }
    .payment-person .pay-group-title {
      margin-bottom: 0.75rem;
    }
    .pay-group-title {
      font-weight: 700;
      font-size: 0.95rem;
      color: var(--zui-headline);
    }
    .pay-group-sub {
      font-size: 0.8rem;
      color: var(--zui-panel-ink-soft);
      margin-bottom: 0.4rem;
    }
    .pay-divider {
      border-top: 1px solid var(--zui-line);
      margin: 0.85rem 0;
    }
    .tier {
      appearance: none;
      width: 100%;
      text-align: left;
      font: inherit;
      background: #fff;
      color: var(--zui-headline);
      display: flex;
      align-items: center;
      gap: 0.6rem;
      border: 1.5px solid var(--zui-line);
      border-radius: 12px;
      padding: 0.6rem 0.75rem;
      cursor: pointer;
      margin-bottom: 0.4rem;
    }
    .tier[aria-pressed="true"] {
      border-color: var(--zui-accent);
      background: var(--zui-accent-soft);
    }
    .tier .price {
      margin-left: auto;
      font-weight: 700;
      color: var(--zui-headline);
    }
    .tier .sub {
      font-size: 0.78rem;
      color: var(--zui-panel-ink-soft);
    }
    .badge {
      font-size: 0.6rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--zui-accent-deep, var(--zui-accent));
    }
    .credit-line {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      font-size: 0.85rem;
      color: var(--zui-headline);
      padding: 0.875rem;
      margin-bottom: 0.5rem;
      border: 1px solid var(--zui-line);
      border-radius: 10px;
      cursor: pointer;
      line-height: 1.45;
    }
    .credit-line.selected {
      border-color: var(--zui-accent);
      background: var(--zui-accent-soft);
    }
    .credit-line input {
      flex: 0 0 auto;
      width: 18px;
      height: 18px;
      margin: 2px 0 0;
      accent-color: var(--zui-accent);
    }
    .credit-copy {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      min-width: 0;
      overflow-wrap: anywhere;
    }
    .credit-title {
      font-weight: 600;
      font-size: 0.9rem;
    }
    .credit-meta {
      font-size: 0.78rem;
      color: var(--zui-panel-ink-soft);
    }
    .credit-line:focus-within,
    .tier:focus-visible,
    .buy-acc-head:focus-visible {
      outline: 2px solid var(--zui-accent);
      outline-offset: 3px;
    }
    .credit-covered {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      font-size: 0.85rem;
      font-weight: 500;
      color: #3a7a4d;
      margin: 0.75rem 0 0;
      line-height: 1.5;
    }
    .credit-covered .chk {
      width: 16px;
      height: 16px;
      border-radius: 999px;
      background: #3a7a4d;
      color: #fff;
      display: grid;
      place-items: center;
      flex: 0 0 auto;
    }
    .buy-acc-head {
      appearance: none;
      background: none;
      border: 0;
      width: 100%;
      text-align: left;
      font: inherit;
      font-size: 0.82rem;
      font-weight: 600;
      color: var(--zui-accent-deep);
      cursor: pointer;
      min-height: 44px;
      margin-top: 0.75rem;
      padding: 0.75rem 0 0;
      display: flex;
      align-items: center;
      gap: 0.35rem;
    }
    .payment-actions {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding-top: 0.25rem;
    }
    .payment-actions .next {
      min-width: 140px;
      min-height: 44px;
    }
    @media (max-width: 600px) {
      .payment-actions .next {
        width: 100%;
      }
    }
    /* order summary aside */
    .summary {
      position: sticky;
      top: 1.75rem;
      padding: 1.25rem;
    }
    @media (max-width: 860px) {
      .summary {
        position: static;
      }
    }
    /* PLAT-6800: no payment option chosen yet — ask instead of quoting $0. */
    .pay-pending {
      margin-top: 1rem;
      padding-top: 1rem;
      border-top: 1px solid var(--zui-line);
      font-size: 14px;
      color: var(--zui-details);
    }
    .pay-pending p {
      margin: 0 0 0.5rem;
    }
    .pay-pending .link {
      appearance: none;
      background: none;
      border: 0;
      padding: 0;
      color: var(--zui-accent);
      font: inherit;
      font-weight: 700;
      cursor: pointer;
      text-decoration: underline;
    }
    .summary h3 {
      font-family: var(--zui-font), system-ui, sans-serif;
      font-weight: 700;
      font-size: 18px;
      letter-spacing: -0.01em;
      color: var(--zui-headline);
      margin: 0 0 1rem;
    }
    .sum-item {
      display: flex;
      gap: 0.75rem;
      align-items: flex-start;
    }
    .sum-item .thumb {
      position: relative;
      width: 56px;
      height: 56px;
      border-radius: 10px;
      overflow: visible;
      flex: 0 0 auto;
      background: var(--zui-accent-soft);
      display: grid;
      place-items: center;
    }
    .sum-item .thumb img {
      width: 100%;
      height: 100%;
      border-radius: 10px;
      object-fit: cover;
      display: block;
    }
    .sum-item .badge-count {
      position: absolute;
      top: -6px;
      left: -6px;
      width: 20px;
      height: 20px;
      border-radius: 999px;
      background: var(--zui-accent);
      color: #fff;
      font-size: 0.7rem;
      font-weight: 700;
      display: grid;
      place-items: center;
      border: 2px solid #fff;
    }
    .sum-item .nm {
      font-weight: 700;
      font-size: 0.9rem;
      color: var(--zui-headline);
    }
    .sum-item .mt {
      font-size: 0.75rem;
      color: var(--zui-panel-ink-soft);
      line-height: 1.35;
    }
    .sum-item .pr {
      margin-left: auto;
      font-weight: 700;
      color: var(--zui-headline);
    }
    .breakdown {
      border-top: 1px dashed var(--zui-line);
      margin-top: 0.875rem;
      padding-top: 0.75rem;
    }
    .li {
      display: flex;
      justify-content: space-between;
      font-size: 0.85rem;
      padding: 0.2rem 0;
      color: var(--zui-panel-ink);
    }
    .li .sub {
      font-size: 0.72rem;
      color: var(--zui-panel-ink-soft);
    }
    .li .free {
      color: #3a7a4d;
      font-weight: 600;
    }
    .promo {
      margin: 0.875rem 0;
    }
    .promo .lbl {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--zui-panel-ink-soft);
      margin-bottom: 0.5rem;
    }
    .promo .row {
      display: flex;
      gap: 0.5rem;
    }
    .promo input {
      flex: 1;
      padding: 0.55rem 0.7rem;
      border: 1px solid var(--zui-line);
      border-radius: 10px;
      background: #f4f2ec;
      color: var(--zui-headline);
      font: inherit;
      min-width: 0;
    }
    .promo button {
      appearance: none;
      border: 1px solid var(--zui-line);
      background: #fff;
      color: var(--zui-headline);
      border-radius: var(--zui-btn-radius, 10px);
      padding: 0 0.9rem;
      font: inherit;
      font-weight: 700;
      font-size: 0.78rem;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      cursor: pointer;
    }
    .promo button:disabled {
      opacity: 0.6;
      cursor: default;
    }
    .promo-msg {
      margin-top: 0.4rem;
      font-size: 0.75rem;
    }
    .promo-msg.err {
      color: #b4541f;
    }
    .promo-msg.ok {
      color: #3a7a4d;
      font-weight: 600;
    }
    .totals .li {
      font-size: 0.9rem;
    }
    /* Qualifies the total directly above it: the tax and fee lines are absent
       because nothing has priced them yet, not because they are zero. */
    .totals-note {
      margin: 0.4rem 0 0;
      font-size: 0.78rem;
      color: var(--zui-muted, #6b7a70);
    }
    .total {
      display: flex;
      justify-content: space-between;
      font-family: var(--zui-font), system-ui, sans-serif;
      font-weight: 800;
      font-size: 1.05rem;
      margin-top: 0.5rem;
      padding-top: 0.5rem;
      border-top: 1px solid var(--zui-line);
      color: var(--zui-headline);
    }
    .ssl {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.4rem;
      margin-top: 1rem;
      padding-top: 0.875rem;
      border-top: 1px solid var(--zui-line);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--zui-panel-ink-soft);
    }
    .next {
      appearance: none;
      align-self: flex-end;
      cursor: pointer;
      border: 0;
      background: var(--zui-accent);
      color: var(--zui-btn-text);
      font-family: var(--zui-font), system-ui, sans-serif;
      font-weight: 700;
      border-radius: var(--zui-btn-radius, 10px);
      padding: 0.75rem 1.75rem;
    }
    .next:disabled,
    .zui-cta:disabled {
      background: #d8d6cf;
      cursor: default;
    }
    .err {
      font-size: 0.78rem;
      color: #b4541f;
    }
    .terms .waiver-body {
      max-height: 160px;
    }
    /* SC10: the terms agreement is a single line, so center the checkbox on
         it (the shared .waiver-check flex-starts for the multi-line waiver copy,
         which left the box sitting above the text here). Tight line-height keeps
         the checkbox and "I agree…" text on a shared baseline. */
    .terms .waiver-check {
      align-items: center;
      line-height: 1.3;
    }
  `,
];
